import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * Deal file type enum (matches backend DealFileType)
 */
export type TDealFileType = "tb_only" | "tb_and_gl";

/**
 * GL type enum (matches backend GLType)
 */
export type TGlType = "none" | "movement" | "mtd" | "ytd";

/**
 * Final destination enum (matches backend FinalFileDestination)
 */
export type TFinalFileDestination = "yardi" | "data_platform";

/**
 * Deal entity (matches backend DealResponse)
 */
export type TDeal = {
    id: string;
    companyId: string;
    name: string;
    code: string | null;
    serviceProviderId: string | null;
    portfolioCompanyId: string | null;
    financeUserIds: string[];
    dealFileType: TDealFileType;
    /**
     * CoA mapping mode of the deal's business partner, read-only here — it is
     * edited on the partner. Drives whether the GL column-mapping screen offers
     * the second source-account field. Absent reads as single-key.
     */
    coaMappingMode?: string;
    glType: TGlType;
    importAvailable: boolean;
    finalFileDestination: TFinalFileDestination;
    requiresSegment2: boolean;
    requiresSegment3: boolean;
    requiresSegment4: boolean;
    requiresSegment5: boolean;
    requiresSegment6: boolean;
    requiresSegment7: boolean;
    segment2MappingType: string;
    segment4MappingType: string;
    segment5MappingType: string;
    segment6MappingType: string;
    segment7MappingType: string;
    monthlyDeadlineDay: number | null;
    quarterlyDeadlineDay: number | null;
    yearEndDeadline: string | null;
    startPeriod: string | null;
    yardiEntityId: string | null;
    yardiDatabase: string | null;
    acceptsOtb: boolean;
    otbComparisonEnabled: boolean;
    acceptsFinJournals: boolean;
    isActive: boolean;
    isTest: boolean;
    notes: string | null;
    tbFilteringRules: Array<{
        filteringColumnHeader: string;
        filteringValue: string[];
        includeFilteredValues: boolean;
    }>;
    glFilteringRules: Array<{
        filteringColumnHeader: string;
        filteringValue: string[];
        includeFilteredValues: boolean;
    }>;
    activatedAt: string | null;
    deactivatedAt: string | null;
    createdAt: string;
    updatedAt: string;
};

/**
 * Deal list filters
 */
export type TDealFilters = {
    serviceProviderId?: string;
    isActive?: boolean;
    searchQuery?: string;
    page?: number;
    pageSize?: number;
};

/**
 * Paginated response type
 */
export type TPaginatedDeals = {
    items: TDeal[];
    total: number;
    page: number;
    pageSize: number;
    totalUnfiltered?: number;
};

/**
 * Deals slice state
 */
export type TDealsState = {
    dealsListState: TAsyncState<TPaginatedDeals>;
    currentDealState: TAsyncState<TDeal>;
    dealFilters: TDealFilters;
    selectedDealId: string | null;
};

/**
 * Deals slice actions
 */
export type TDealsActions = {
    // List operations
    setDealsLoading: () => void;
    setDealsList: (data: TPaginatedDeals) => void;
    setDealsError: (error: Error) => void;
    setDealFilters: (filters: Partial<TDealFilters>) => void;
    resetDealFilters: () => void;

    // Current deal operations
    setCurrentDealLoading: () => void;
    setCurrentDeal: (deal: TDeal) => void;
    setCurrentDealError: (error: Error) => void;
    clearCurrentDeal: () => void;
    updateCurrentDeal: (updates: Partial<TDeal>) => void;

    // Selection
    setSelectedDealId: (dealId: string | null) => void;
};

/**
 * Complete deals slice type
 */
export type TSliceDeals = TDealsState & TDealsActions;

const defaultFilters: TDealFilters = {
    isActive: true,
    page: 1,
    pageSize: 50,
};

const initialState: TDealsState = {
    dealsListState: initialAsyncState(),
    currentDealState: initialAsyncState(),
    dealFilters: defaultFilters,
    selectedDealId: null,
};

/**
 * Creates the deals slice for the store.
 * Handles deal state and operations.
 */
export const createSliceDeals = (): TStateCreator<TSliceDeals> => {
    return (set) => ({
        ...initialState,

        // List operations
        setDealsLoading: () => {
            set((state) => ({
                ...state,
                dealsListState: pendingAsyncState(state.dealsListState.data),
            }));
        },

        setDealsList: (data: TPaginatedDeals) => {
            set({ dealsListState: fulfilledAsyncState(data) });
        },

        setDealsError: (error: Error) => {
            set((state) => ({
                ...state,
                dealsListState: rejectedAsyncState(error, state.dealsListState.data),
            }));
        },

        setDealFilters: (filters: Partial<TDealFilters>) => {
            set((state) => ({
                ...state,
                dealFilters: { ...state.dealFilters, ...filters },
            }));
        },

        resetDealFilters: () => {
            set({ dealFilters: defaultFilters });
        },

        // Current deal operations
        setCurrentDealLoading: () => {
            set((state) => ({
                ...state,
                currentDealState: pendingAsyncState(state.currentDealState.data),
            }));
        },

        setCurrentDeal: (deal: TDeal) => {
            set({ currentDealState: fulfilledAsyncState(deal) });
        },

        setCurrentDealError: (error: Error) => {
            set({ currentDealState: rejectedAsyncState(error) });
        },

        clearCurrentDeal: () => {
            set({ currentDealState: initialAsyncState() });
        },

        updateCurrentDeal: (updates: Partial<TDeal>) => {
            set((state) => {
                if (state.currentDealState.data) {
                    return {
                        ...state,
                        currentDealState: fulfilledAsyncState({
                            ...state.currentDealState.data,
                            ...updates,
                        }),
                    };
                }
                return state;
            });
        },

        // Selection
        setSelectedDealId: (dealId: string | null) => {
            set({ selectedDealId: dealId });
        },
    });
};
